import { useState, useEffect, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import qs from 'qs';
import { API_SERVICE } from '@/api/interview-plan';
import { getScorecardInitialValues } from '@/utils/schedule-interview-helpers';
import { useInterviewStageManager } from '@/context-providers/interviewStageManager';
import { useSelector } from 'react-redux';
import { getAtsJobCreate } from '@/store/atsJobCreate';

export const useScorecardTemplates = (
  questions: any,
  scorecard: {
    feedback_description: string;
    rating_description: string;
    scorecard_feedback_description: string;
    scorecard_rating_description: string;
  }
) => {
  const {
    scorecard_feedback_description,
    scorecard_rating_description,
    feedback_description,
    rating_description,
  } = scorecard || {};
  const { currentJob } = useSelector(getAtsJobCreate);
  const currentJobData = currentJob?.data || {};
  let defaultScorecardParams;
  const { department, office, employment_type } =
    currentJobData?.basic_info || {};
  const basicInfo = currentJobData?.basic_info || {};
  const { isWorkTypesEnabled } = useWorkTypeOptions();
  if (currentJobData?.basic_info) {
    defaultScorecardParams = {
      department_ids: [department.id],
      office_ids: office?.map((itr) => itr.id),
      // Contract C: flag ON scopes by work type id, OFF by the legacy enum.
      ...workTypeScopeParam(isWorkTypesEnabled, {
        atsWorkTypeIds: [readJobWorkTypeId(basicInfo)],
        employmentTypes: [employment_type],
      }),
    };
  }

  const { activityAction } = useInterviewStageManager();
  const [changeTemplateInProgress, setChangeTemplateInProgress] =
    useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const templateParams = useMemo(() => {
    if (defaultScorecardParams) {
      return qs.stringify(defaultScorecardParams, {
        arrayFormat: 'brackets',
      });
    }
  }, [defaultScorecardParams]);

  const { data: scorecardTemplates = [] } = useQuery({
    queryKey: [`scorecard_templates`, templateParams],
    queryFn: () => API_SERVICE.getScoreCardTemplates(templateParams),
  });

  const templateOptions = useMemo(
    () =>
      (scorecardTemplates ?? []).map(
        ({
          name,
          questions: templateQuestions,
          feedback_description,
          is_feedback_mandatory,
          rating_description,
        }) => ({
          value: name,
          label: name,
          questions: templateQuestions,
          feedback_description: feedback_description,
          is_feedback_mandatory: is_feedback_mandatory ?? false,
          rating_description: rating_description,
        })
      ),
    [scorecardTemplates]
  );

  const initialValues = useMemo(() => {
    const templateData = selectedTemplate || {
      questions,
      feedback_description:
        feedback_description || scorecard_feedback_description,
      is_feedback_mandatory:
        (scorecard as any)?.scorecard_config?.is_feedback_mandatory ??
        (scorecard as any)?.is_feedback_mandatory ??
        false,
      rating_description: rating_description || scorecard_rating_description,
    };

    const scoreCards = getScorecardInitialValues(templateData.questions);
    return {
      scoreCards,
      feedback_description: templateData.feedback_description || '',
      is_feedback_mandatory: templateData.is_feedback_mandatory ?? false,
      rating_description: templateData.rating_description || '',
    };
  }, [
    questions,
    feedback_description,
    scorecard_feedback_description,
    rating_description,
    scorecard_rating_description,
    selectedTemplate,
    scorecard,
  ]);

  const handleTemplateOnChange = useCallback(
    (selectedTemplate: {
      rating_description: string;
      feedback_description: string;
      questions: any;
    }) => {
      setChangeTemplateInProgress(true);
      setSelectedTemplate(selectedTemplate);
      setTimeout(() => setChangeTemplateInProgress(false), 100);
    },
    []
  );

  useEffect(() => {
    if (!scorecardTemplates.length || questions?.length) return;
    if (activityAction?.type !== 'new') return;

    const defaultTemplate = templateOptions[0];
    if (!defaultTemplate) return;

    const timerId = setTimeout(() => {
      handleTemplateOnChange(defaultTemplate);
    }, 0);
    return () => clearTimeout(timerId);
  }, [
    scorecardTemplates,
    questions?.length,
    activityAction?.type,
    templateOptions,
    handleTemplateOnChange,
  ]);

  return {
    templateOptions,
    handleTemplateOnChange,
    initialValues,
    changeTemplateInProgress,
  };
};
import {
  readJobWorkTypeId,
  useWorkTypeOptions,
  workTypeScopeParam,
} from '@/api/work-types';
